import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../../lib/api-client'
import { type AccessAddress, type AddressSearchResponse, formatAdgangsadresse } from './address'

interface Props {
  value: string
  onChange: (value: string) => void
  onSelectHit?: (hit: AccessAddress) => void
  error?: string
  label?: string
  placeholder?: string
  autoFocus?: boolean
}

export function AddressAutocomplete({
  value, onChange, onSelectHit, error, label, placeholder, autoFocus,
}: Props) {
  const [query, setQuery] = useState(value)
  const [hits, setHits] = useState<AccessAddress[]>([])
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleInput = (text: string) => {
    setQuery(text)
    onChange(text)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (text.length < 3) { setHits([]); setOpen(false); return }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch<AddressSearchResponse<AccessAddress>>(
          `/api/v1/properties/addresses/search?q=${encodeURIComponent(text)}`
        )
        setUnavailable(!res.available)
        setHits(res.items)
        setOpen(res.items.length > 0)
      } catch {
        setUnavailable(true)
        setHits([])
        setOpen(false)
      }
    }, 220)
  }

  const handleSelect = (hit: AccessAddress) => {
    const formatted = formatAdgangsadresse(hit)
    setQuery(formatted)
    onChange(formatted)
    setHits([])
    setOpen(false)
    onSelectHit?.(hit)
  }

  const borderColor = error ? '#dc2626' : focused ? '#4f46e5' : '#e8ecf4'
  const boxShadow = focused && !error ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none'

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
          {label}
        </label>
      )}
      <input
        type="text"
        value={query}
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={e => handleInput(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          height: 40,
          padding: '0 12px',
          borderRadius: 7,
          border: `1px solid ${borderColor}`,
          boxShadow,
          fontSize: 14,
          color: '#0f172a',
          background: '#ffffff',
          outline: 'none',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
          transition: 'border-color 0.12s, box-shadow 0.12s',
        }}
      />
      {open && hits.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 2px)',
          left: 0,
          right: 0,
          background: '#ffffff',
          border: '1px solid #e8ecf4',
          borderRadius: 7,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          zIndex: 200,
          maxHeight: 280,
          overflowY: 'auto',
        }}>
          {hits.map((hit) => (
            <SuggestionRow
              key={hit.id}
              label={formatAdgangsadresse(hit)}
              onSelect={() => handleSelect(hit)}
            />
          ))}
        </div>
      )}
      {error && <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{error}</p>}
      {unavailable && !error && (
        <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          Adressesøgning er ikke tilgængelig lige nu — indtast adressen manuelt.
        </p>
      )}
    </div>
  )
}

function SuggestionRow({ label, onSelect }: { label: string; onSelect: () => void }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseDown={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '9px 14px',
        fontSize: 14,
        color: '#0f172a',
        cursor: 'pointer',
        background: hovered ? '#f8fafc' : '#ffffff',
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      {label}
    </div>
  )
}
